import { Link } from "react-router-dom";
import { ArrowRight, Printer, Mail, FileText, Building2, Shield, Waves, Plane, ShoppingBag, Trophy, MapPin, Calendar, DollarSign, Factory, CheckCircle2, ExternalLink } from "lucide-react";

/**
 * Living Waters US — Exclusive Sales & Distribution Proposal
 * Proposer: Sterile Mate LLC DBA Bleu Flux
 * Public route: /proposals/living-waters
 * Downloadable via browser Print → Save as PDF
 */
export default function LivingWatersProposalPage() {
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const publicBase = (process.env.REACT_APP_PUBLIC_URL || "https://sterilemate.com").replace(/\/$/, "");
  const backendBase = (process.env.REACT_APP_BACKEND_URL || publicBase).replace(/\/$/, "");
  const proposalUrl = `${publicBase}/proposals/living-waters`;
  const pdfUrl = `${backendBase}/api/proposals/living-waters/pdf`;

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  const emailSubject =
    "Exclusive 5-Year Sales & Distribution Proposal — Living Waters US × Sterile Mate LLC DBA Bleu Flux";
  const emailBody = `Dear Living Waters US Leadership,

Please find enclosed our formal proposal for Sterile Mate LLC DBA Bleu Flux to serve as the primary EXCLUSIVE Sales & Distribution arm for Living Waters US — "Hydration for Every Moment."

Highlights:
• 5-year exclusive term (1-year initial + 4-year renewal)
• Channels: Government / Municipal contracting, Retail placement, Tradeshows & Sponsorships, Airport Concessions
• 15% commission on the current $655/case wholesale price
• Backed by Sterile Mate's active vendor status with the City of Houston, Harris County, and the State of Texas — including five (5) county bids valued in the seven figures and ten (10) state water-purification contracts already awarded

Full proposal (view online): ${proposalUrl}

We are prepared to hold a working session at your convenience to walk you through channel-by-channel rollout, capacity coverage, and the exclusivity framework.

Respectfully,
George Broughton III
President — Sterile Mate LLC DBA Bleu Flux
george.broughton@sterilemate.com · 800.988.3892`;

  const mailtoHref = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  // Capacity math (built once, referenced below)
  const casesPerPallet = 72;
  const palletsPerDay = 14;
  const casesPerDay = casesPerPallet * palletsPerDay; // 1,008
  const wholesalePerCase = 655;
  const commissionPct = 0.15;
  const dailyGrossRevenue = casesPerDay * wholesalePerCase; // $660,240
  const dailyCommission = dailyGrossRevenue * commissionPct; // $99,036
  const workingDays = 250;
  const annualGrossFullRun = dailyGrossRevenue * workingDays; // $165.06M
  const annualCommissionFullRun = dailyCommission * workingDays; // $24.76M

  const fmt = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  const fmtUsd = (n) => `$${fmt(n)}`;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4 print:bg-white print:py-0" data-testid="living-waters-proposal-page">

      {/* Toolbar (hidden on print) */}
      <div className="max-w-5xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link
          to="/proposals"
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-800 hover:text-blue-950"
          data-testid="lw-proposal-back-to-index"
        >
          <FileText className="w-4 h-4" />
          All Proposals
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/living-waters"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-semibold"
            data-testid="lw-proposal-view-landing-btn"
          >
            <ExternalLink className="w-4 h-4" />
            View Marketing Site
          </Link>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-bold shadow-lg shadow-amber-500/20"
            data-testid="lw-proposal-download-pdf-btn"
          >
            <FileText className="w-4 h-4" />
            Download PDF
          </a>
          <a
            href={mailtoHref}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-800 hover:bg-blue-900 text-white text-sm font-semibold"
            data-testid="lw-proposal-email-btn"
          >
            <Mail className="w-4 h-4" />
            Email
          </a>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold"
            data-testid="lw-proposal-print-btn"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      {/* Proposal document */}
      <article
        className="max-w-5xl mx-auto bg-white shadow-xl rounded-lg text-slate-900 print:shadow-none print:rounded-none"
        data-testid="living-waters-proposal-document"
      >
        {/* HERO / COVER */}
        <div className="relative overflow-hidden rounded-t-lg print:rounded-none">
          <img
            src={`${backendBase}/api/static/email_assets/lw_proposal_hero.jpg`}
            alt="Living Waters — Hydration for Every Moment"
            className="w-full h-72 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-blue-900/80 to-cyan-800/70 flex flex-col justify-center px-12">
            <p className="text-xs font-bold tracking-[0.3em] text-amber-400 uppercase mb-3">Confidential · Exclusive Distribution Proposal</p>
            <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Living Waters US<br/>
              <span className="text-amber-400">Hydration for Every Moment.</span>
            </h1>
            <p className="mt-4 text-blue-100 text-lg max-w-2xl leading-relaxed">
              A 5-year exclusive Sales &amp; Distribution partnership proposal from <span className="font-bold text-white">Sterile Mate LLC DBA Bleu Flux</span> — moving Living Waters into government, retail, tradeshow, and airport channels across Texas and nationally.
            </p>
          </div>
        </div>

        {/* LETTERHEAD */}
        <header className="px-12 pt-10 pb-6 border-b-2 border-blue-900 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-blue-950 leading-tight">Sterile Mate LLC</h2>
            <p className="text-sm font-semibold text-cyan-700 mt-1">DBA <span className="text-slate-800">Bleu Flux</span> — Beverage &amp; Water Distribution Division</p>
            <p className="text-xs text-slate-500 mt-2">
              Houston, TX · george.broughton@sterilemate.com · 800.988.3892 · sterilemate.com
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-500">Confidential</p>
            <p className="text-sm text-slate-700 mt-1" data-testid="lw-proposal-date">{today}</p>
            <p className="text-xs text-slate-500 mt-1">Proposal ref: SM-BF-LW-2026-001</p>
          </div>
        </header>

        {/* RECIPIENT + RE */}
        <section className="px-12 pt-8 pb-2 text-sm leading-6">
          <p className="font-semibold text-slate-900">Living Waters US Leadership</p>
          <p className="text-slate-700">Founders &middot; Chief Executive Officer &middot; VP of Sales &amp; Distribution</p>
          <p className="text-slate-700">Living Waters US &mdash; Hydration for Every Moment</p>

          <p className="mt-6 text-sm">
            <span className="font-semibold text-blue-950">RE:&nbsp;</span>
            Proposal for a 5-Year Exclusive Sales &amp; Distribution Agreement &mdash; Government
            Contracting, Retail Placement, Tradeshows &amp; Sponsorships, and Airport Concessions &mdash;
            Sterile Mate LLC DBA Bleu Flux as Primary Distribution Arm.
          </p>
        </section>

        {/* SALUTATION + INTRO */}
        <section className="px-12 pt-6 pb-2 text-[15px] leading-7 text-slate-800">
          <p>Dear Living Waters US Leadership,</p>
          <p className="mt-5">
            Thank you for the opportunity to formally submit this proposal. <span className="font-semibold">Sterile Mate LLC, doing business as Bleu Flux</span>, is prepared to serve as the <span className="font-semibold">primary and exclusive Sales &amp; Distribution arm</span> for Living Waters US &mdash; representing the brand across government contracting, national retail placement, tradeshows and sponsorship event activation, and airport concession programs. This is a natural extension of the vendor infrastructure we have already built serving the City of Houston, Harris County, the State of Texas, and multiple healthcare systems as a licensed water-purification and medical device supplier.
          </p>

          {/* Companion Marketing Site Callout — hidden on print */}
          <div className="print:hidden mt-6 rounded-xl bg-gradient-to-r from-cyan-50 via-blue-50 to-cyan-50 border border-cyan-200 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="lw-proposal-marketing-callout">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-700 flex items-center justify-center flex-shrink-0">
                <Waves className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-widest text-cyan-700 uppercase">Companion Marketing Site</p>
                <p className="mt-1 text-sm text-slate-800 leading-6">
                  Prefer the visual walkthrough? Everything in this proposal &mdash; branded, with imagery of the four channels &mdash; is one click away at the <span className="font-semibold text-blue-950">Living Waters &times; Bleu Flux</span> landing page.
                </p>
              </div>
            </div>
            <Link
              to="/living-waters"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-white font-bold text-sm shadow-md whitespace-nowrap"
              data-testid="lw-proposal-inline-marketing-link"
            >
              <ExternalLink className="w-4 h-4" />
              Open Marketing Site
            </Link>
          </div>
        </section>

        {/* PROPOSAL AT A GLANCE */}
        <section className="px-12 py-8">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-3">Proposal at a Glance</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-blue-950 text-white rounded-lg p-5">
              <Calendar className="w-6 h-6 text-amber-400 mb-2"/>
              <p className="text-3xl font-black text-amber-400">5 yrs</p>
              <p className="text-xs uppercase tracking-wider text-blue-200 mt-1">Exclusive term</p>
              <p className="text-[11px] text-blue-100 mt-1">1-year initial + 4-year renewal</p>
            </div>
            <div className="bg-blue-950 text-white rounded-lg p-5">
              <DollarSign className="w-6 h-6 text-amber-400 mb-2"/>
              <p className="text-3xl font-black text-amber-400">15%</p>
              <p className="text-xs uppercase tracking-wider text-blue-200 mt-1">Commission of wholesale</p>
              <p className="text-[11px] text-blue-100 mt-1">$0.057 per bottle to Bleu Flux</p>
            </div>
            <div className="bg-blue-950 text-white rounded-lg p-5">
              <Factory className="w-6 h-6 text-amber-400 mb-2"/>
              <p className="text-3xl font-black text-amber-400">14/day</p>
              <p className="text-xs uppercase tracking-wider text-blue-200 mt-1">Pallets · 5 days/week</p>
              <p className="text-[11px] text-blue-100 mt-1">24,192 bottles · 1,008 cases</p>
            </div>
            <div className="bg-blue-950 text-white rounded-lg p-5">
              <Trophy className="w-6 h-6 text-amber-400 mb-2"/>
              <p className="text-3xl font-black text-amber-400">4</p>
              <p className="text-xs uppercase tracking-wider text-blue-200 mt-1">Distribution channels</p>
              <p className="text-[11px] text-blue-100 mt-1">Gov · Retail · Events · Airport</p>
            </div>
          </div>
        </section>

        {/* STRATEGIC POSITION */}
        <section className="px-12 py-6 bg-slate-50 border-y border-slate-200">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-2">Why Bleu Flux — Strategic Position Already In Place</h3>
          <h4 className="text-2xl font-bold text-blue-950 leading-tight mb-4">
            You are not hiring a startup distributor.<br/>
            You are inheriting an active government vendor stack.
          </h4>
          <p className="text-[15px] text-slate-700 leading-7 mb-5">
            Sterile Mate LLC operates today as an active, credentialed vendor to the largest municipal and state buyers in Texas. These vendor relationships took years to earn &mdash; and every one of them consumes bottled water on a repeating institutional basis (jail intake, first-response staging, EOC deployments, court facilities, park pavilions, veteran events, disaster relief cache). This is exactly where Living Waters belongs, and we are already inside the tent.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-5 border border-slate-200">
              <div className="flex items-start gap-3">
                <Building2 className="w-6 h-6 text-blue-800 flex-shrink-0 mt-0.5"/>
                <div>
                  <p className="font-bold text-blue-950">City of Houston &middot; Harris County &middot; State of Texas</p>
                  <p className="text-sm text-slate-700 mt-1 leading-6">Active registered vendor across all three tiers as a <span className="font-semibold">Medical Device Supplier</span> and <span className="font-semibold">Water Purification Infrastructure Partner</span>. HUB-certified. Master vendor files current. Insurance and bonding in place.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 border border-slate-200">
              <div className="flex items-start gap-3">
                <Trophy className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5"/>
                <div>
                  <p className="font-bold text-blue-950">5 County Bids · 7-Figure Values</p>
                  <p className="text-sm text-slate-700 mt-1 leading-6">Five county contract wins across Texas with individual awards in the <span className="font-semibold">seven-figure range</span>. Established Buy Board, TIPS, BuyBoard-Coop, and direct county purchasing-office pathways.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 border border-slate-200">
              <div className="flex items-start gap-3">
                <Waves className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5"/>
                <div>
                  <p className="font-bold text-blue-950">10 State Contracts · Water Purification Systems</p>
                  <p className="text-sm text-slate-700 mt-1 leading-6">Ten currently active state-level contracts for water purification and treatment infrastructure. The buyer&apos;s desk that signs those POs is the same desk that reorders bottled water &mdash; and they already know us.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 border border-slate-200">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5"/>
                <div>
                  <p className="font-bold text-blue-950">Healthcare Vendor Placement</p>
                  <p className="text-sm text-slate-700 mt-1 leading-6">Credentialed vendor to Harris Health, Memorial Hermann, and additional health systems. Hospital cafeterias, patient-experience programs, employee wellness, and community-health events all buy bottled water &mdash; usually from three different distributors. We consolidate that to one.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-5 border border-slate-200 md:col-span-2">
              <div className="flex items-start gap-3">
                <Plane className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5"/>
                <div>
                  <p className="font-bold text-blue-950">Airport Concession Placement Capability</p>
                  <p className="text-sm text-slate-700 mt-1 leading-6">Direct pathway into <span className="font-semibold">Houston Airport System (IAH · HOU · EFD)</span> concession programs, with active MWBE/HUB certification and existing City of Houston vendor status positioning Living Waters for both terminal retail placement and airline crew-provisioning programs. Nationally scalable through DBE partnerships at DFW, ATL, LAX, and additional Class-1 hubs.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CHANNELS */}
        <section className="px-12 py-8">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-2">Four Distribution Channels — One Exclusive Partner</h3>
          <h4 className="text-2xl font-bold text-blue-950 leading-tight mb-5">Where Living Waters ends up on the shelf, in the crate, or in the cooler.</h4>
          <div className="space-y-4">
            {[
              {
                icon: Building2,
                color: "text-blue-700",
                bg: "bg-blue-50",
                title: "1. Government &amp; Municipal Contracting",
                body: "Direct placement into City of Houston (Public Works, Parks, HFD, HPD, Municipal Courts), Harris County (Sheriff, EOC, Public Health, Community Services), and State of Texas contract vehicles (TxSmartBuy, DIR, TIPS, BuyBoard, TXMAS). Our HUB certification unlocks minority-set-aside line items. Repeating institutional volume &mdash; not one-off events."
              },
              {
                icon: ShoppingBag,
                color: "text-emerald-700",
                bg: "bg-emerald-50",
                title: "2. Retail &amp; Convenience Placement",
                body: "Regional grocery (H-E-B, Kroger regional buyer), independent retail chains, ethnic and specialty grocery in metro Houston, DFW, Austin, and San Antonio. Convenience-store DSD routes through partnered wholesalers. Institutional cafeterias, corporate campuses, and healthcare cafeteria placements leveraging our existing hospital vendor relationships."
              },
              {
                icon: Trophy,
                color: "text-amber-600",
                bg: "bg-amber-50",
                title: "3. Tradeshows &amp; Sponsorship Event Placement",
                body: "Bleu Flux operates a Houston-based event and hospitality footprint with turnkey pallet-delivery, cooler-provisioning, and branded activation capability. Target verticals: healthcare conferences (AAMI, APIC, HIDA, AONL), municipal-safety expos (TML, TAC), veteran and community events (5K races, festivals, faith-based conferences), and B2B trade expos. Every event becomes a branded impression."
              },
              {
                icon: Plane,
                color: "text-indigo-700",
                bg: "bg-indigo-50",
                title: "4. Airport Concession Placement",
                body: "Terminal retail (Hudson, Paradies-Lagardère sub-agreements), airline crew-provisioning programs, and airport lounge placements. Anchored by our City of Houston vendor status and HUB certification &mdash; the two credentials that unlock Houston Airport System (IAH · HOU · EFD) concession opportunities. Scalable to DFW, ATL, LAX and other majors through DBE joint-venture pathways."
              },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className={`rounded-lg border border-slate-200 p-5 flex gap-4 ${c.bg}`}>
                  <div className="flex-shrink-0">
                    <Icon className={`w-10 h-10 ${c.color}`} />
                  </div>
                  <div>
                    <p className="font-bold text-blue-950 text-base" dangerouslySetInnerHTML={{ __html: c.title }} />
                    <p className="text-sm text-slate-700 mt-2 leading-6">{c.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* COMMERCIAL FRAMEWORK */}
        <section className="px-12 py-8 bg-slate-50 border-y border-slate-200">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-2">Commercial Framework</h3>
          <h4 className="text-2xl font-bold text-blue-950 leading-tight mb-5">Simple. Aggressive. Aligned with production.</h4>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Terms */}
            <div className="bg-white rounded-lg p-6 border border-slate-200">
              <h5 className="text-sm font-bold text-blue-950 uppercase tracking-wider mb-3">Deal Terms</h5>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="py-2 text-slate-600">Term</td><td className="py-2 font-semibold text-slate-900 text-right">5 years (1&#8209;year initial + 4&#8209;year auto-renewal)</td></tr>
                  <tr><td className="py-2 text-slate-600">Territory</td><td className="py-2 font-semibold text-slate-900 text-right">United States (Exclusive)</td></tr>
                  <tr><td className="py-2 text-slate-600">Exclusivity</td><td className="py-2 font-semibold text-slate-900 text-right">Primary &amp; Exclusive Sales &amp; Distribution</td></tr>
                  <tr><td className="py-2 text-slate-600">Product</td><td className="py-2 font-semibold text-slate-900 text-right">16.9 oz BPA-free PET bottles</td></tr>
                  <tr><td className="py-2 text-slate-600">Pack Configuration</td><td className="py-2 font-semibold text-slate-900 text-right">24 bottles / case &middot; 72 cases / pallet</td></tr>
                  <tr><td className="py-2 text-slate-600">Wholesale Reference Price</td><td className="py-2 font-semibold text-slate-900 text-right">$655 / case</td></tr>
                  <tr><td className="py-2 text-slate-600">Commission</td><td className="py-2 font-bold text-amber-600 text-right">15% of wholesale = $98.25 / case</td></tr>
                  <tr><td className="py-2 text-slate-600">Contract-Win Bonus</td><td className="py-2 font-semibold text-slate-900 text-right">15% commission applies to <em>all</em> awarded revenue &mdash; RFP wins included</td></tr>
                  <tr><td className="py-2 text-slate-600">Production Capacity</td><td className="py-2 font-semibold text-slate-900 text-right">14 pallets / day  <span className="text-slate-500">({fmt(casesPerDay)} cases · {fmt(casesPerDay*24)} bottles)</span></td></tr>
                </tbody>
              </table>
            </div>

            {/* Capacity math */}
            <div className="bg-blue-950 text-white rounded-lg p-6">
              <h5 className="text-sm font-bold text-amber-400 uppercase tracking-wider mb-3">Capacity Math &mdash; What 14 Pallets / Day Actually Means</h5>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-blue-800 pb-2">
                  <span className="text-blue-200">14 pallets × 72 cases</span>
                  <span className="font-mono font-bold">{fmt(casesPerDay)} cases / day</span>
                </div>
                <div className="flex justify-between border-b border-blue-800 pb-2">
                  <span className="text-blue-200">{fmt(casesPerDay)} cases × $655</span>
                  <span className="font-mono font-bold">{fmtUsd(dailyGrossRevenue)} / day</span>
                </div>
                <div className="flex justify-between border-b border-blue-800 pb-2">
                  <span className="text-blue-200">× 250 working days</span>
                  <span className="font-mono font-bold">{fmtUsd(annualGrossFullRun)} / yr</span>
                </div>
                <div className="flex justify-between border-b border-blue-800 pb-2">
                  <span className="text-blue-200">15% commission (daily)</span>
                  <span className="font-mono font-bold text-amber-400">{fmtUsd(dailyCommission)} / day</span>
                </div>
                <div className="flex justify-between pt-1">
                  <span className="text-amber-400 font-bold">Commission @ full run (yr)</span>
                  <span className="font-mono font-bold text-amber-400 text-lg">{fmtUsd(annualCommissionFullRun)}</span>
                </div>
              </div>
              <p className="text-[11px] text-blue-200 mt-4 leading-5">
                Ceiling scenario at 100% production absorption. Realistic Year&nbsp;1 target: 40&ndash;55% absorption via government &amp; healthcare institutional channels; retail and airport concessions ramp in Year&nbsp;2. Living Waters retains 85% of every sale &mdash; ${fmt(wholesalePerCase*0.85)}/case (gross before COGS).
              </p>
            </div>
          </div>

          {/* Ramp scenario */}
          <div className="mt-6 bg-white rounded-lg p-6 border border-slate-200">
            <h5 className="text-sm font-bold text-blue-950 uppercase tracking-wider mb-4">5-Year Ramp Scenario (Illustrative Absorption of Production Capacity)</h5>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 font-semibold">Contract Year</th>
                  <th className="text-right py-2 font-semibold">Capacity Absorption</th>
                  <th className="text-right py-2 font-semibold">Annual Cases</th>
                  <th className="text-right py-2 font-semibold">Wholesale Gross</th>
                  <th className="text-right py-2 font-semibold">Bleu Flux 15%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ["Year 1 (Initial)", 0.30],
                  ["Year 2", 0.50],
                  ["Year 3", 0.70],
                  ["Year 4", 0.85],
                  ["Year 5", 0.95],
                ].map(([label, pct]) => {
                  const cases = Math.round(casesPerDay * workingDays * pct);
                  const gross = cases * wholesalePerCase;
                  const comm = gross * commissionPct;
                  return (
                    <tr key={label}>
                      <td className="py-2 font-semibold text-slate-800">{label}</td>
                      <td className="py-2 text-right text-slate-700">{Math.round(pct * 100)}%</td>
                      <td className="py-2 text-right font-mono text-slate-700">{fmt(cases)}</td>
                      <td className="py-2 text-right font-mono text-slate-900 font-semibold">{fmtUsd(gross)}</td>
                      <td className="py-2 text-right font-mono text-amber-600 font-bold">{fmtUsd(comm)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-blue-950">
                <tr className="text-blue-950 font-bold">
                  <td className="pt-3">5-Year Illustrative Total</td>
                  <td className="pt-3 text-right">&mdash;</td>
                  <td className="pt-3 text-right font-mono">
                    {fmt(Math.round(casesPerDay * workingDays * (0.30+0.50+0.70+0.85+0.95)))}
                  </td>
                  <td className="pt-3 text-right font-mono">
                    {fmtUsd(Math.round(casesPerDay * workingDays * wholesalePerCase * (0.30+0.50+0.70+0.85+0.95)))}
                  </td>
                  <td className="pt-3 text-right font-mono text-amber-600">
                    {fmtUsd(Math.round(casesPerDay * workingDays * wholesalePerCase * commissionPct * (0.30+0.50+0.70+0.85+0.95)))}
                  </td>
                </tr>
              </tfoot>
            </table>
            <p className="text-[11px] text-slate-500 mt-3 leading-5">
              Illustrative model. Actual absorption depends on RFP timing, retail placement pacing, and event calendar. Model assumes 250 working days/yr and constant $655/case wholesale reference &mdash; not a guarantee. Volume-tier discounts to be negotiated separately for institutional multi-pallet awards.
            </p>
          </div>
        </section>

        {/* WHAT BLEU FLUX DELIVERS */}
        <section className="px-12 py-8">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-2">What Bleu Flux Delivers Under This Agreement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {[
              "Vendor-of-record activation for Living Waters across City of Houston, Harris County, and State of Texas contract vehicles",
              "HUB / MWBE / DBE certification pass-through for minority-set-aside line items",
              "Dedicated RFP response desk &mdash; every bid, every deadline, professionally submitted",
              "Retail buyer meetings with H-E-B, Kroger, and independent grocery regional buyers",
              "Full tradeshow and sponsorship activation team &mdash; booth logistics, pallet delivery, branded coolers",
              "Airport concession introductions via Houston Airport System master concessionaires",
              "Fleet + last-mile logistics using our existing Houston distribution footprint",
              "Marketing amplification through AskIsaac (our internal AI platform) and the sterilemate.com / bleuflux domain assets",
              "Chain-of-custody, lot traceability, and audit-ready delivery documentation (required by government purchasers)",
              "Monthly reporting: pipeline, orders won, orders shipped, commission earned &mdash; transparent scoreboard",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 leading-6">{item}</p>
              </div>
            ))}
          </div>
        </section>

        {/* WHAT LIVING WATERS PROVIDES */}
        <section className="px-12 py-8 bg-slate-50 border-y border-slate-200">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-2">What Living Waters Provides in Return</h3>
          <ul className="mt-3 space-y-2 text-[15px] text-slate-800 leading-7">
            <li className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-blue-800 flex-shrink-0 mt-1" />
              <span><span className="font-semibold">Exclusive US Sales &amp; Distribution rights</span> for Sterile Mate LLC DBA Bleu Flux for the initial 1-year term, with automatic 4-year renewal contingent upon Bleu Flux achieving Year&nbsp;1 minimum capacity absorption target (to be jointly set at contract execution; recommended range: 20&ndash;30% of stated production capacity).</span>
            </li>
            <li className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-blue-800 flex-shrink-0 mt-1" />
              <span><span className="font-semibold">Production commitment</span> of up to 14 pallets/day dedicated to Bleu Flux&#8209;originated purchase orders; production scheduling coordination via a shared operating cadence (weekly forecast, monthly review).</span>
            </li>
            <li className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-blue-800 flex-shrink-0 mt-1" />
              <span><span className="font-semibold">Firm wholesale reference price of $655 / case</span>, with 45-day advance notice on any wholesale-price adjustment and grandfathering of all in-flight Bleu Flux-negotiated commitments.</span>
            </li>
            <li className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-blue-800 flex-shrink-0 mt-1" />
              <span><span className="font-semibold">Brand assets &amp; marketing collateral</span> (logo, product imagery, spec sheets, case study permissions) for use in RFP responses, retail buyer decks, event branding, and airport concession applications.</span>
            </li>
            <li className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-blue-800 flex-shrink-0 mt-1" />
              <span><span className="font-semibold">Certificates of Analysis (COA), FDA registration documentation, source-water audit trail</span> &mdash; the compliance stack required by institutional and government buyers.</span>
            </li>
          </ul>
        </section>

        {/* TIMELINE */}
        <section className="px-12 py-8">
          <h3 className="text-xs font-bold tracking-[0.25em] text-cyan-700 uppercase mb-4">Rollout Timeline &mdash; First 90 Days</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { day: "Day 0 – 15", title: "Contract execution & vendor onboarding", body: "Master Distribution Agreement signed. Bleu Flux adds Living Waters SKUs to our City of Houston, Harris County, and State of Texas vendor files. Marketing collateral kit finalized. Initial 5-pallet trial inventory positioned in Houston." },
              { day: "Day 16 – 45", title: "First RFP submissions & retail meetings", body: "Submissions into open TIPS / BuyBoard / TXMAS bottled-water line items. First retail buyer meetings (H-E-B, Kroger regional). Airport concessionaire outreach opened (Hudson, Paradies)." },
              { day: "Day 46 – 90", title: "First contract wins & scaled shipments", body: "First institutional purchase orders released. Retail placement decisions returned. Live Bleu Flux monthly scorecard delivered to Living Waters leadership. Renewal target (Year 2) benchmark established." },
            ].map((step) => (
              <div key={step.day} className="rounded-lg border border-slate-200 p-5 bg-white">
                <p className="text-xs font-bold text-amber-600 uppercase tracking-widest">{step.day}</p>
                <p className="mt-2 font-bold text-blue-950">{step.title}</p>
                <p className="mt-2 text-sm text-slate-700 leading-6">{step.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CLOSE */}
        <section className="px-12 py-8 border-t border-slate-200">
          <p className="text-[15px] text-slate-800 leading-7">
            Living Waters US is a brand built to be poured everywhere hydration matters. Bleu Flux is the vehicle already in motion &mdash; the vendor stack, the certifications, the government relationships, and the healthcare footprint are in place today. This proposal aligns both sides on a fair, aggressive, and transparent commercial framework that rewards results and protects your brand&apos;s pricing integrity.
          </p>
          <p className="mt-4 text-[15px] text-slate-800 leading-7">
            We welcome a working session at your convenience to walk through channel-by-channel rollout, exclusivity language, and Year&nbsp;1 target-setting. We are prepared to move to execution on this proposal within 30 days.
          </p>
          <p className="mt-6 text-[15px] text-slate-800">
            Respectfully,
          </p>
          <div className="mt-8">
            <p className="text-lg font-bold text-blue-950">George Broughton III</p>
            <p className="text-sm text-slate-700">President &mdash; Sterile Mate LLC DBA Bleu Flux</p>
            <p className="text-xs text-slate-500 mt-1">Houston, Texas · 800.988.3892 · george.broughton@sterilemate.com</p>
          </div>
        </section>

        {/* SIGNATURE BLOCK (for print) */}
        <section className="px-12 pb-12 print:page-break-before-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t-2 border-blue-950">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Accepted &amp; Agreed &mdash; Living Waters US</p>
              <div className="mt-6 h-px bg-slate-400" />
              <p className="text-xs text-slate-600 mt-1">Signature</p>
              <div className="mt-6 h-px bg-slate-400" />
              <p className="text-xs text-slate-600 mt-1">Printed Name &amp; Title</p>
              <div className="mt-6 h-px bg-slate-400" />
              <p className="text-xs text-slate-600 mt-1">Date</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Accepted &amp; Agreed &mdash; Sterile Mate LLC DBA Bleu Flux</p>
              <div className="mt-6 h-px bg-slate-400" />
              <p className="text-xs text-slate-600 mt-1">George Broughton III &mdash; President</p>
              <div className="mt-6 h-px bg-slate-400" />
              <p className="text-xs text-slate-600 mt-1">Signature</p>
              <div className="mt-6 h-px bg-slate-400" />
              <p className="text-xs text-slate-600 mt-1">Date</p>
            </div>
          </div>
        </section>

        {/* FOOTER STRIP */}
        <footer className="bg-blue-950 text-blue-200 px-12 py-4 text-xs flex justify-between items-center rounded-b-lg print:rounded-none">
          <span>Sterile Mate LLC DBA Bleu Flux &middot; Confidential Proposal &middot; SM-BF-LW-2026-001</span>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Houston, TX &middot; sterilemate.com</span>
        </footer>
      </article>

      {/* Print CSS tweak — legacy browsers */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
